import { useState } from "react";

import { pmndpAPI } from "../../../services/pmndp.api";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader";
import PMNDPModal from './PMNDPModal';
import {
    PMNDPFormField,
    PMNDPFormSection,
    PMNDPFormButtons
} from './PMNDPForm';
import styles from './pmndp-enhanced.module.css';

const CompleteDialysis = ({
  onClose,
  patient_name,
  patient_id,
  last_dialysis,
  dialysis_id,
  abha_number,
  abha_address,
  age,
  gender,
  mobile,
  getDiaList, // This should be a function: () => getDiaList(page, searchFields)
}) => {
  const [isPageLoading, setIsPageLoading] = useState(false);

  const [form, setForm] = useState({
    // Authentication
    otp: "",
    otp_code: "",
    mode: "Mobile",

    // Patient Info
    patient_id: patient_id || "",
    mobile: mobile || "",
    abha_number: abha_number || "",
    abha_address: abha_address || "",
    patient_name: patient_name || "",
    age: age || "",
    gender: gender || "",

    // Medical Data
    post_blood_pres: "",
    post_weight: "",
    hemoglobin: "",
    kt: "",
    serum: "",

    // Status
    status: "Completed",
    reason: "",
    remarks: "",
  });

  const handleChange = (e) => {
    try {
      const { name, value } = e.target;
      if (!name) {
        console.error("Input field missing name attribute");
        return;
      }
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    } catch (error) {
      console.error("Error handling form change:", error);
      toast.error("Error updating form field");
    }
  };

  const handleModeChange = async (e) => {
    try {
      const value = e.target.value;
      if (!value) {
        toast.error("Please select a valid mode");
        return;
      }

      setForm((prev) => ({
        ...prev,
        mode: value,
      }));

      if (value === "OTP") {
        if (!dialysis_id) {
          toast.error("Dialysis ID is required to send OTP");
          return;
        }

        setIsPageLoading(true);
        const resp = await pmndpAPI.sendCompleteDialysisOtp({
          dialysis_id: dialysis_id,
        });
        if (resp && resp.success) {
          setIsPageLoading(false);
          toast.success(`OTP sent successfully.`);
        } else {
          const errorMsg = resp?.msg || resp?.message || "Failed to send OTP.";
          toast.error(errorMsg, { className: "custom-toast-error" });
          setIsPageLoading(false);
        }
      }
    } catch (err) {
      setIsPageLoading(false);
      console.error("Error in mode change:", err);
      const errorMsg =
        err?.response?.data?.message || err?.message || "Error sending OTP.";
      toast.error(errorMsg, { className: "custom-toast-error" });
    } finally {
      if (isPageLoading) setIsPageLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      otp: form.otp,
      otp_code: "dialysis_success",
      patient_id: patient_id,
      dialysis_id: dialysis_id,
      post_blood_pres: form.post_blood_pres,
      post_weight: form.post_weight,
      hemoglobin: form.hemoglobin,
      kt: form.kt,
      serum: form.serum,
      status: form.status,
      failed_reason: form.status === "Failed" ? form.reason : "",
      remarks: form.status === "Failed" ? form.remarks : "",
    };

    try {
      setIsPageLoading(true);
      const resp = await pmndpAPI.verifyPatientOtpComplete(payload);
      if (resp && resp.success) {
        toast.success(`OTP verified successfully.`);
        setTimeout(() => {
          try {
            if (typeof onClose === "function") onClose();
            if (typeof getDiaList === "function") getDiaList();
          } catch (callbackError) {
            console.error("Error in callback functions:", callbackError);
            toast.error("Error occurred after verification");
          } finally {
            setIsPageLoading(false);
          }
        }, 1200);
      } else {
        const errorMsg = resp?.msg || resp?.message || "Failed to verify OTP.";
        toast.error(errorMsg, { className: "custom-toast-error" });
      }
    } catch (err) {
      console.error("Error verifying OTP:", err);
      const errorMsg =
        err?.response?.data?.message || err?.message || "Error verifying OTP.";
      toast.error(errorMsg, { className: "custom-toast-error" });
    } finally {
      setIsPageLoading(false);
    }
  };

  return (
    <>
      {isPageLoading && <Loader />}

      <PMNDPModal
        isOpen={true}
        onClose={onClose}
        title="Complete Session"
        icon="fa-check-circle"
        size="xlarge"
        footer={
          <PMNDPFormButtons
            onSave={handleSubmit}
            onCancel={onClose}
            saveText="Verify"
            cancelText="Cancel"
            isLoading={isPageLoading}
          />
        }
      >
        <form onSubmit={handleSubmit}>
          {/* ================= Personal Details ================= */}
          <div className="startDialysisSection">
            <h4>Personal Details</h4>
            <div className="startDiyFeiSec">
              <div className="row">
                <div className="col-xs-12 col-md-6">
                  <div className="feild">
                    <label>ABHA Number/Health ID</label>
                    <input
                      type="text"
                      name="abha_number"
                      value={form.abha_number}
                      readOnly
                    />
                  </div>
                </div>

                <div className="col-xs-12 col-md-6">
                  <div className="feild">
                    <label>ABHA Address</label>
                    <input
                      type="text"
                      name="abha_address"
                      value={form.abha_address}
                      readOnly
                    />
                  </div>
                </div>

                <div className="col-xs-12 col-md-4">
                  <div className="feild">
                    <label>Patient Name</label>
                    <input
                      type="text"
                      name="patient_name"
                      value={form.patient_name}
                      readOnly
                    />
                  </div>
                </div>

                <div className="col-xs-12 col-md-2">
                  <div className="feild">
                    <label>Age</label>
                    <input type="number" name="age" value={form.age} readOnly />
                  </div>
                </div>

                <div className="col-xs-12 col-md-3">
                  <div className="feild">
                    <label>Gender</label>
                    <select value={form.gender} disabled>
                      <option>Male</option>
                      <option>Female</option>
                      <option>Transgender</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ================= Health Record ================= */}
          <div className="startDialysisSection">
            <h4>Health Record Details</h4>
            <div className="startDiyFeiSec">
              <div className="row">
                <div className="col-md-3">
                  <div className="feild">
                    <label>Blood Pressure</label>
                    <input
                      name="post_blood_pres"
                      value={form.post_blood_pres}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="feild">
                    <label>Weight</label>
                    <input
                      name="post_weight"
                      value={form.post_weight}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="feild">
                    <label>KT/V</label>
                    <input name="kt" value={form.kt} onChange={handleChange} />
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="feild">
                    <label>Serum Creatinine</label>
                    <input
                      name="serum"
                      value={form.serum}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ================= Contact ================= */}
          <div className="startDialysisSection">
            <h4>Contact Details</h4>
            <div className="startDiyFeiSec">
              <div className="row">
                <div className="col-md-4">
                  <div className="feild">
                    <label>Mobile No.</label>
                    <input value={form.mobile} readOnly />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ================= Dialysis Status ================= */}
          <div className="startDialysisSection">
            <h4>Dialysis in Progress</h4>
            <div className="startDiyFeiSec">
              <div className="row">
                <div className="col-md-4">
                  <div className="feild">
                    <label>Started At</label>
                    <input value={last_dialysis} readOnly />
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="feild">
                    <label>Status</label>
                    <select
                      name="status"
                      value={form.status}
                      onChange={handleChange}
                    >
                      <option value="Completed">Completed</option>
                      <option value="Failed">Failed</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ================= Failure Reason ================= */}
          {form.status === "Failed" && (
            <div className="startDialysisSection">
              <h4>Reason</h4>
              <div className="startDiyFeiSec">
                <div className="row">
                  <div className="col-md-4">
                    <div className="feild">
                      <label>Failed Reason</label>
                      <select
                        name="reason"
                        value={form.reason}
                        onChange={handleChange}
                      >
                        <option value="">Select</option>
                        <option>Cancelled by Facility</option>
                        <option>Cancelled by Patient</option>
                        <option>Technical Error</option>
                        <option>Machine Fault</option>
                        <option>Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="feild">
                      <label>Remarks</label>
                      <input
                        name="remarks"
                        value={form.remarks}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ================= Verification Mode ================= */}
          <div className="startDialysisSection">
            <h4>Select Mode</h4>
            <div className="startDiyFeiSec">
              <div className="row">
                <div className="col-md-4">
                  <div className="feild">
                    <label>Select Mode</label>
                    <select
                      name="mode"
                      value={form.mode}
                      onChange={handleModeChange}
                    >
                      <option value="">Select</option>
                      <option value="OTP">OTP</option>
                      <option value="Biometric">Biometric</option>
                    </select>
                  </div>
                </div>

                {form.mode === "OTP" && (
                  <div className="col-md-4">
                    <div className="feild">
                      <label>Enter OTP</label>
                      <input
                        name="otp"
                        value={form.otp}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>
      </PMNDPModal>
    </>
  );
};

export default CompleteDialysis;
